import type { NavItem } from '@/components/layout/Sidebar';
import { getAllAppNavConfigs } from '@/config/appNavigation';
import { masterAdminNav } from '@/config/navigation';
import {
  getModuleAdminNavItems,
  LAUNCHER_ERP_MODULES,
} from '@/config/moduleDefaults';
import {
  getModuleWorkNavItems,
  moduleApprovalsDelegationsPath,
} from '@/config/moduleWorkPaths';
import { pathToNavKey } from '@/i18n/navLabel';
import { i18n } from '@/i18n';
import {
  SETTINGS_CATEGORIES,
  SETTINGS_PATH,
  SETTINGS_MASTERS,
} from '@/config/settingsRegistry';

import {
  HR_EMPLOYEE_SELF_SERVICE_NAV,
  HR_MANAGER_SELF_SERVICE_NAV,
} from '@/config/hrSelfServicePortals';

/** Routes registered for access control but omitted from the sidebar. */
const EXTRA_ROUTE_TITLES: Record<string, string> = {
  '/hr/leave-policies': 'Leave policies',
  '/hr/leaves': 'Leave requests',
  '/hr/loans': 'Employee loans',
  '/hr/ess': HR_EMPLOYEE_SELF_SERVICE_NAV,
  '/hr/mss': HR_MANAGER_SELF_SERVICE_NAV,
  '/crm/customers': 'Customer profile',
  '/desk/tickets': 'Tickets',
  '/desk/support': 'Customer support',
  '/account/profile': 'My profile',
  '/onboarding': 'Onboarding',
  '/onboarding/products': 'Product selection',
  '/pos/dashboard': 'Dashboard',
  '/lead-gen': 'Lead Generation',
};

function addNavItem(map: Map<string, string>, item: NavItem): void {
  const path = item.to.split('?')[0];
  map.set(path, item.label);
}

function buildRoutePageTitleMap(): Map<string, string> {
  const map = new Map<string, string>();

  for (const config of getAllAppNavConfigs()) {
    for (const group of config.groups) {
      for (const item of group.items) {
        addNavItem(map, item);
      }
    }
  }

  for (const module of LAUNCHER_ERP_MODULES) {
    for (const item of getModuleWorkNavItems(module)) {
      addNavItem(map, item);
    }
    for (const item of getModuleAdminNavItems(module)) {
      addNavItem(map, item);
    }
    map.set(moduleApprovalsDelegationsPath(module), 'Approval delegations');
  }

  for (const item of masterAdminNav) {
    addNavItem(map, item);
  }

  for (const [path, label] of Object.entries(EXTRA_ROUTE_TITLES)) {
    map.set(path, label);
  }

  map.set(SETTINGS_PATH, 'Settings');
  for (const category of SETTINGS_CATEGORIES) {
    map.set(`${SETTINGS_PATH}/${category.key}`, category.label);
  }
  for (const master of SETTINGS_MASTERS) {
    map.set(master.route, master.label);
  }

  return map;
}

const ROUTE_PAGE_TITLES = buildRoutePageTitleMap();

export function getRoutePageTitleMap(): ReadonlyMap<string, string> {
  return ROUTE_PAGE_TITLES;
}

function normalizePath(pathname: string): string {
  const base = pathname.split('?')[0].replace(/\/+$/, '');
  return base || '/';
}

function resolveEnglishRouteLabel(path: string): string | undefined {
  const exact = ROUTE_PAGE_TITLES.get(path);
  if (exact) return exact;

  const prefix = [...ROUTE_PAGE_TITLES.keys()]
    .filter((route) => {
      if (route === '/pos') return path === '/pos';
      return path.startsWith(`${route}/`);
    })
    .sort((a, b) => b.length - a.length)[0];

  return prefix ? ROUTE_PAGE_TITLES.get(prefix) : undefined;
}

/** English sidebar label for a route (before i18n). */
export function getEnglishRoutePageTitle(pathname: string): string | undefined {
  return resolveEnglishRouteLabel(normalizePath(pathname));
}

function localizeTitle(path: string, english: string | undefined): string | undefined {
  if (!english) return undefined;
  if (!i18n.isInitialized) return english;
  const key = pathToNavKey(path.replace(/^\//, ''));
  return i18n.t(key, { ns: 'navigation', defaultValue: english });
}

/** Page heading aligned with the sidebar label for this route. */
export function resolveRoutePageTitle(pathname: string): string | undefined {
  const path = normalizePath(pathname);
  const english = resolveEnglishRouteLabel(path);
  return english ? localizeTitle(path, english) : undefined;
}
